/**
 * The short-lived auth attribution that travels across the Google OAuth redirect and the MFA
 * route. It is intentionally session-scoped: it is analytics context, not authentication state,
 * and must never be written into the shared `authStore` blob consumed by v3.
 */
const STORAGE_KEY = 'orphex_auth_attribution'

export type AuthAttribution = { kind: 'login'; method: string } | { kind: 'signup'; method: string }

function read(): AuthAttribution | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as Partial<AuthAttribution>
    if (
      (value.kind !== 'login' && value.kind !== 'signup') ||
      typeof value.method !== 'string' ||
      value.method.trim() === ''
    ) {
      return null
    }
    return { kind: value.kind, method: value.method }
  } catch {
    return null
  }
}

function write(value: AuthAttribution): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Analytics attribution must never block authentication when storage is unavailable.
  }
}

export function markLogin(method: string): void {
  write({ kind: 'login', method })
}

export function markSignup(method: string): void {
  write({ kind: 'signup', method })
}

/** Consume once, after the authenticated bootstrap has the user/account identity. */
export function consumeAuthAttribution(): AuthAttribution | null {
  const value = read()
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // A failed cleanup is harmless; the next successful boot will still be guarded by the caller.
  }
  return value
}

export function clearAuthAttribution(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Best-effort logout cleanup.
  }
}
